/**
 * Audit Document Filters Hook
 *
 * Client-side filter, sort, and search logic for documents in the audit
 * Documents tab. Takes pre-fetched DocumentView[] and DocumentFilters (from
 * useDocumentFilters()) and returns filtered + grouped documents.
 */

import { useMemo } from "react";
import type { DocumentView } from "@/packages/types";

interface DocumentFilters {
  search?: string;
  tagIds?: string[];
  fileType?: string;
  statuses?: string[];
  statusOperator?: "is" | "is_not";
  categoryIds?: string[];
  categoryOperator?: "is" | "is_not";
  sortBy?: string;
  sortOrder?: "asc" | "desc";
}

interface UseAuditDocumentFiltersInput {
  allDocuments: DocumentView[];
  filters: DocumentFilters;
}

interface UseAuditDocumentFiltersReturn {
  documents: DocumentView[];
  groupedDocuments: Map<string, DocumentView[]>;
}

export function useAuditDocumentFilters({
  allDocuments,
  filters,
}: UseAuditDocumentFiltersInput): UseAuditDocumentFiltersReturn {
  const documents = useMemo(() => {
    let result = allDocuments;

    // Search filter
    if (filters.search) {
      const search = filters.search.toLowerCase();
      result = result.filter(
        (doc) =>
          doc.title.toLowerCase().includes(search) ||
          doc.description?.toLowerCase().includes(search),
      );
    }

    // Status filter
    if (filters.statuses && filters.statuses.length > 0) {
      const { statuses, statusOperator } = filters;
      result = result.filter((doc) => {
        const matches = statuses.includes(doc.status);
        return statusOperator === "is_not" ? !matches : matches;
      });
    }

    // File type filter
    if (filters.fileType) {
      const { fileType } = filters;
      result = result.filter((doc) => doc.type === fileType);
    }

    // Tags filter
    if (filters.tagIds && filters.tagIds.length > 0) {
      const { tagIds } = filters;
      result = result.filter((doc) =>
        doc.tags?.some((t) => tagIds.includes(t.id)),
      );
    }

    // Category filter
    if (filters.categoryIds && filters.categoryIds.length > 0) {
      const { categoryIds, categoryOperator } = filters;
      result = result.filter((doc) => {
        if (!doc.categoryId) return categoryOperator === "is_not";
        const matches = categoryIds.includes(doc.categoryId);
        return categoryOperator === "is_not" ? !matches : matches;
      });
    }

    // Sort
    if (filters.sortBy) {
      const { sortBy, sortOrder = "asc" } = filters;
      result = [...result].sort((a, b) => {
        let cmp = 0;
        switch (sortBy) {
          case "uploadedAt":
            cmp = a.updatedAt.localeCompare(b.updatedAt);
            break;
          case "fileName":
            cmp = a.title.localeCompare(b.title);
            break;
          case "fileType":
            cmp = a.type.localeCompare(b.type);
            break;
          case "status":
            cmp = a.status.localeCompare(b.status);
            break;
        }
        // Default date sort is descending (newest first)
        if (sortBy === "uploadedAt") {
          return sortOrder === "asc" ? cmp : -cmp;
        }
        return sortOrder === "desc" ? -cmp : cmp;
      });
    }

    return result;
  }, [allDocuments, filters]);

  const groupedDocuments = useMemo(() => {
    const groups = new Map<string, DocumentView[]>();
    for (const doc of documents) {
      const key = doc.categoryId ?? "uncategorized";
      const existing = groups.get(key) ?? [];
      existing.push(doc);
      groups.set(key, existing);
    }
    return groups;
  }, [documents]);

  return { documents, groupedDocuments };
}
